export type HeatmapMetric = 'pnl' | 'delta' | 'gamma' | 'vega' | 'theta';

export type HeatmapValueCell = {
  pnl: number;
  delta: number;
  gamma: number;
  vega: number;
  theta: number;
};

export const toDollarValue = (
  cell: HeatmapValueCell,
  price: number,
  metric: HeatmapMetric,
): number => {
  switch (metric) {
    case 'delta':
      return cell.delta * 100 * price;
    case 'gamma':
      return cell.gamma * 100 * price * price * 0.01;
    case 'vega':
      return cell.vega * 100;
    case 'theta':
      return cell.theta * 100;
    case 'pnl':
    default:
      return cell.pnl;
  }
};

// Metric options for the heatmap toolbar selector. Greek metrics are shown in
// dollar-notional terms.
export const HEATMAP_METRICS: { value: HeatmapMetric; label: string }[] = [
  { value: 'pnl', label: 'P&L' },
  { value: 'delta', label: 'Delta' },
  { value: 'gamma', label: 'Gamma' },
  { value: 'vega', label: 'Vega' },
  { value: 'theta', label: 'Theta' },
];

const niceStepCeiling = (raw: number): number => {
  if (!(raw > 0) || !Number.isFinite(raw)) {
    return 1;
  }

  const base = Math.pow(10, Math.floor(Math.log10(raw)));
  const fraction = raw / base;
  if (fraction <= 1) {
    return base;
  }
  if (fraction <= 2) {
    return 2 * base;
  }
  if (fraction <= 5) {
    return 5 * base;
  }
  return 10 * base;
};

/** Build an exact-size, positive, nice-step grid covering spot and all strikes. */
export const buildHeatmapPrices = (
  currentPrice: number,
  strikes: readonly number[],
  numRows: number,
  rangeFactor: number,
): number[] => {
  if (!(currentPrice > 0) || !Number.isFinite(currentPrice) || numRows < 2) {
    return [];
  }

  const validStrikes = strikes.filter(
    (strike) => Number.isFinite(strike) && strike > 0,
  );
  const references = [currentPrice, ...validStrikes];
  const referenceLow = Math.min(...references);
  const referenceHigh = Math.max(...references);
  const referenceSpan = Math.max(
    referenceHigh - referenceLow,
    currentPrice * 0.24,
  );
  // Range controls the padding without ever cropping a position strike.
  const padding =
    Math.max(referenceSpan * 0.2, currentPrice * 0.05) *
    Math.max(0, rangeFactor);
  const desiredLow = Math.max(Number.EPSILON, referenceLow - padding);
  const desiredHigh = referenceHigh + padding;

  const step = niceStepCeiling((desiredHigh - desiredLow) / (numRows - 1));
  const gridSpan = step * (numRows - 1);
  const minStart = desiredHigh - gridSpan;
  const maxStart = desiredLow;
  // On a multiple of the step, so rows line up with strikes for most symbols.
  const centeredStart = Math.round((currentPrice - gridSpan / 2) / step) * step;
  // An offset grid still has a nice interval when a very low strike and a
  // very high strike cannot both sit on positive multiples of that interval.
  const start = Math.max(
    Number.EPSILON,
    Math.min(maxStart, Math.max(minStart, centeredStart)),
  );

  return Array.from(
    { length: numRows },
    (_, index) => start + (numRows - 1 - index) * step,
  );
};

/** Snap each price to its nearest listed strike, dropping rows that land on the same strike. */
export const snapToListedStrikes = (
  prices: readonly number[],
  listedStrikes: readonly number[],
): number[] => {
  const strikes = [...new Set(listedStrikes)].filter(
    (strike) => Number.isFinite(strike) && strike > 0,
  );
  if (strikes.length === 0) {
    return [...prices];
  }
  const nearestStrike = (price: number) =>
    strikes.reduce((best, strike) =>
      Math.abs(strike - price) < Math.abs(best - price) ? strike : best,
    );
  return [...new Set(prices.map(nearestStrike))];
};

/** Evenly subsample real timestamps while retaining the first and last. */
export const subsampleHeatmapDates = (
  timestamps: readonly number[],
  count: number,
): number[] => {
  const unique = [...new Set(timestamps)].sort((a, b) => a - b);
  if (count < 2 || unique.length <= count) {
    return unique;
  }

  return Array.from({ length: count }, (_, index) => {
    const sourceIndex = Math.round((index * (unique.length - 1)) / (count - 1));
    return unique[sourceIndex];
  });
};
